/**
 * THE ACTIVE BRAND — Zenvoya, plus at most one brand folder laid over it.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *   brands/zenvoya/brand.js   the base: every value, always present
 *   brands/<name>/brand.js    optional: only what that brand changes
 *
 * The overlay is found by folder, not configured: put a `<name>/brand.js` here
 * to apply it, delete the folder to go back to Zenvoya. Same idea as
 * app-frontend's `src/branding/tenants/<name>/`.
 *
 * A folder only counts when its name is the brand's own `id` (`navi/` holds
 * `id: 'navi'`). So RENAMING it — `navi_old/`, `navi-backup/` — switches the
 * brand off just like deleting it, and keeps the files to switch back. With
 * more than one active folder the first (alphabetically) wins — keep one.
 *
 * Components never import this; they read `colors.*` / `brand` from
 * `theme/tokens`, which are built from it.
 */
import zenvoya from './zenvoya/brand';

/**
 * The overlay brand, or null. `require.context` is webpack's — under Jest it
 * does not exist, so tests always run as Zenvoya.
 */
const findOverlay = () => {
  try {
    const ctx = require.context('./', true, /^\.\/(?!zenvoya\/)[^/]+\/brand\.js$/);
    const active = ctx
      .keys()
      .sort()
      .map((key) => ({ folder: key.split('/')[1], brand: ctx(key).default }))
      .find(({ folder, brand }) => brand && brand.id === folder);
    return active ? active.brand : null;
  } catch {
    return null;
  }
};

/** Overlay a brand onto the base, one group deep (`colors.sidebar` etc.). */
const overlay = (base, over) => {
  if (!over) return base;
  const colors = { ...base.colors };
  Object.entries(over.colors || {}).forEach(([group, value]) => {
    colors[group] = value && typeof value === 'object' ? { ...base.colors[group], ...value } : value;
  });
  return { ...base, ...over, colors };
};

const activeBrand = overlay(zenvoya, findOverlay());

/** Point the tab icon at the brand's favicon (index.html ships Zenvoya's). */
export const applyBrandFavicon = () => {
  if (!activeBrand.favicon || typeof document === 'undefined') return;
  const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
  link.rel = 'icon';
  link.removeAttribute('type');
  link.href = activeBrand.favicon;
  document.head.appendChild(link);
};

export default activeBrand;
